/**
 * =============================================================================
 * Componente: StepScriptSheet
 * Versão: 1.0.0
 *
 * Descrição:
 *   Sheet lateral para configurar o **Roteiro** de um step de Playbook
 *   de Vendas (Receita Previsível). Possui 4 abas:
 *     - Objetivo  (campo curto)
 *     - Script    (rich-text com variáveis dinâmicas + preview)
 *     - Critérios (checklist de sucesso)
 *     - Objeções  (FAQ pergunta/resposta-rich-text)
 *
 *   Usado pelo editor de playbook (CrmPlaybookEditor) para edição;
 *   leitura/execução pelo vendedor é feita em outro componente
 *   (ExecuteStepSheet).
 *
 * Release notes:
 *   v1.0.0 - Criação (Playbook v2.0).
 * =============================================================================
 */
import { useMemo, useRef, useState } from 'react';
import {
  Plus,
  Trash2,
  Eye,
  Code2,
  Target,
  ListChecks,
  MessageSquareWarning,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { VariablePicker } from './VariablePicker';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { RichTextEditor, type RichTextEditorHandle } from '@/components/ui/rich-text-editor';
import { Textarea } from '@/components/ui/textarea';

import {
  PREVIEW_CONTEXT,
  extractVariables,
  renderScript,
} from '@/lib/playbookScript';

import type {
  SuccessCriterion,
  StepObjection,
  UpsertPlaybookStepInput,
} from '@/hooks/useActivityPlaybooks';

// Limites consistentes com o backend (upsert_activity_playbook v2.0.0)
const SCRIPT_MAX = 20000;
const GOAL_MAX = 200;
const ITEMS_MAX = 30;

interface StepScriptSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  step: UpsertPlaybookStepInput & { _key?: string };
  stepLabel: string; // ex.: "Step 2 — Ligação"
  onSave: (
    patch: Pick<
      UpsertPlaybookStepInput,
      'goal' | 'script_html' | 'success_criteria' | 'objections' | 'script_variables_used'
    >,
  ) => void;
}

// Cria id estável para itens recém-adicionados
const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export function StepScriptSheet({
  open,
  onOpenChange,
  step,
  stepLabel,
  onSave,
}: StepScriptSheetProps) {
  // Estado local — só é commitado para o pai ao clicar "Salvar"
  const [goal, setGoal] = useState<string>(step.goal ?? '');
  const [scriptHtml, setScriptHtml] = useState<string>(step.script_html ?? '');
  const [criteria, setCriteria] = useState<SuccessCriterion[]>(
    step.success_criteria ?? [],
  );
  const [objections, setObjections] = useState<StepObjection[]>(
    step.objections ?? [],
  );
  const [showPreview, setShowPreview] = useState(false);

  // Reseta estado quando o step trocar (abrir outro step no mesmo Sheet)
  // Usamos uma key derivada do _key + open para forçar remount nos useState.
  // Aqui mantemos simples: reset manual ao abrir/fechar via key prop no parent.

  const variablesUsed = useMemo(() => {
    const a = extractVariables(scriptHtml);
    const b = objections.flatMap((o) => extractVariables(o.answer_html));
    return Array.from(new Set([...a, ...b]));
  }, [scriptHtml, objections]);

  // Ref para o editor — permite inserir variável na posição do cursor
  const editorRef = useRef<RichTextEditorHandle>(null);

  const insertToken = (token: string) => {
    const snippet = `{{${token}}}`;
    // Se o editor estiver montado (modo edição), insere na posição do cursor.
    // Caso contrário (preview ligado), faz fallback para append no estado.
    if (editorRef.current) {
      editorRef.current.insertText(snippet);
    } else {
      setScriptHtml((cur) => `${cur}${snippet}`);
    }
  };

  const handleSave = () => {
    // Validação leve no frontend (backend re-valida tudo)
    if (goal.length > GOAL_MAX) return;
    if (scriptHtml.length > SCRIPT_MAX) return;
    onSave({
      goal: goal.trim() || null,
      script_html: scriptHtml.trim() || null,
      success_criteria: criteria.filter((c) => c.label.trim().length > 0),
      objections: objections.filter((o) => o.question.trim().length > 0),
      script_variables_used: variablesUsed,
    });
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl p-0 flex flex-col gap-0"
      >
        <SheetHeader className="p-4 border-b shrink-0">
          <SheetTitle className="text-base">Roteiro do step</SheetTitle>
          <SheetDescription className="text-xs">{stepLabel}</SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="goal" className="flex-1 flex flex-col min-h-0">
          <TabsList className="m-3 mb-0 grid grid-cols-4 shrink-0">
            <TabsTrigger value="goal" className="gap-1.5">
              <Target className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Objetivo</span>
            </TabsTrigger>
            <TabsTrigger value="script" className="gap-1.5">
              <Code2 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Script</span>
            </TabsTrigger>
            <TabsTrigger value="criteria" className="gap-1.5">
              <ListChecks className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Critérios</span>
            </TabsTrigger>
            <TabsTrigger value="objections" className="gap-1.5">
              <MessageSquareWarning className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Objeções</span>
            </TabsTrigger>
          </TabsList>

          {/* ---- Objetivo ---- */}
          <TabsContent value="goal" className="flex-1 overflow-y-auto p-4 space-y-3 mt-0">
            <Label htmlFor="step-goal" className="text-sm">
              Qual o objetivo deste step?
            </Label>
            <Input
              id="step-goal"
              value={goal}
              onChange={(e) => setGoal(e.target.value.slice(0, GOAL_MAX))}
              placeholder='Ex.: "Qualificar BANT" ou "Confirmar interesse em proposta"'
              maxLength={GOAL_MAX}
            />
            <p className="text-xs text-muted-foreground">
              {goal.length}/{GOAL_MAX} caracteres. Frase curta que ajuda o vendedor a focar
              no resultado esperado.
            </p>
          </TabsContent>

          {/* ---- Script ---- */}
          <TabsContent value="script" className="flex-1 overflow-y-auto p-4 space-y-3 mt-0">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-sm">Roteiro / Script</Label>
              <div className="flex items-center gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setShowPreview(!showPreview)}
                    >
                      {showPreview ? (
                        <ToggleRight className="h-4 w-4 text-emerald-600" />
                      ) : (
                        <ToggleLeft className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {showPreview ? 'Desativar pré-visualização' : 'Ativar pré-visualização'}
                  </TooltipContent>
                </Tooltip>
                <Label htmlFor="prev" className="text-xs flex items-center gap-1">
                  <Eye className="h-3 w-3" /> Pré-visualizar
                </Label>
              </div>
            </div>

            {/* Picker de variáveis com abas por grupo + busca */}
            <VariablePicker onInsert={insertToken} />

            {showPreview ? (
              <div
                className="prose prose-sm max-w-none border rounded-md p-3 bg-background min-h-[200px]"
                dangerouslySetInnerHTML={{ __html: renderScript(scriptHtml, PREVIEW_CONTEXT) }}
              />
            ) : (
              <RichTextEditor
                ref={editorRef}
                content={scriptHtml}
                onChange={(html) => setScriptHtml(html.slice(0, SCRIPT_MAX))}
                placeholder='Ex.: "Olá {{contact.first_name}}, aqui é a {{seller.first_name}} da {{tenant.name}}..."'
                minHeight="220px"
                maxHeight="380px"
              />
            )}
            <p className="text-xs text-muted-foreground">
              {scriptHtml.length}/{SCRIPT_MAX} caracteres.
              {variablesUsed.length > 0 && (
                <>
                  {' '}
                  Variáveis em uso:{' '}
                  {variablesUsed.map((v) => (
                    <Badge key={v} variant="outline" className="ml-1 text-[10px]">
                      {v}
                    </Badge>
                  ))}
                </>
              )}
            </p>
          </TabsContent>

          {/* ---- Critérios ---- */}
          <TabsContent value="criteria" className="flex-1 overflow-y-auto p-4 space-y-2 mt-0">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Critérios de sucesso</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={criteria.length >= ITEMS_MAX}
                onClick={() =>
                  setCriteria((p) => [...p, { id: newId(), label: '', required: true }])
                }
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Adicionar
              </Button>
            </div>
            <p className="text-xs text-muted-foreground -mt-1">
              Checklist que o vendedor confirma ao executar (ex.: "Identifiquei o decisor").
            </p>
            <ScrollArea className="max-h-[55vh]">
              <div className="space-y-2 pr-2">
                {criteria.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-6">
                    Nenhum critério ainda.
                  </p>
                )}
                {criteria.map((c, idx) => (
                  <div key={c.id} className="flex items-center gap-2 border rounded-md p-2">
                    <Badge variant="outline" className="font-mono text-[10px]">
                      {idx + 1}
                    </Badge>
                    <Input
                      value={c.label}
                      onChange={(e) =>
                        setCriteria((p) =>
                          p.map((x) => (x.id === c.id ? { ...x, label: e.target.value } : x)),
                        )
                      }
                      placeholder="Descrição do critério"
                      className="h-8 flex-1"
                      maxLength={200}
                    />
                    <div className="flex items-center gap-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() =>
                              setCriteria((p) =>
                                p.map((x) =>
                                  x.id === c.id ? { ...x, required: !x.required } : x,
                                ),
                              )
                            }
                          >
                            {c.required ? (
                              <ToggleRight className="h-4 w-4 text-emerald-600" />
                            ) : (
                              <ToggleLeft className="h-4 w-4 text-muted-foreground" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {c.required ? 'Tornar opcional' : 'Tornar obrigatório'}
                        </TooltipContent>
                      </Tooltip>
                      <Label className="text-[11px]">Obrigatório</Label>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-destructive"
                      onClick={() => setCriteria((p) => p.filter((x) => x.id !== c.id))}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* ---- Objeções ---- */}
          <TabsContent value="objections" className="flex-1 overflow-y-auto p-4 space-y-2 mt-0">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Tratamento de objeções</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={objections.length >= ITEMS_MAX}
                onClick={() =>
                  setObjections((p) => [
                    ...p,
                    { id: newId(), question: '', answer_html: '' },
                  ])
                }
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Adicionar
              </Button>
            </div>
            <p className="text-xs text-muted-foreground -mt-1">
              FAQ rápido para o vendedor consultar quando o lead apresentar uma objeção.
            </p>
            <Accordion type="multiple" className="space-y-2">
              {objections.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-6">
                  Nenhuma objeção registrada.
                </p>
              )}
              {objections.map((o, idx) => (
                <AccordionItem
                  key={o.id}
                  value={o.id}
                  className="border rounded-md px-2"
                >
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="font-mono text-[10px]">
                      {idx + 1}
                    </Badge>
                    <AccordionTrigger className="flex-1 py-2 text-sm">
                      {o.question.trim() || `Objeção ${idx + 1}`}
                    </AccordionTrigger>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-destructive"
                      onClick={() =>
                        setObjections((p) => p.filter((x) => x.id !== o.id))
                      }
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <AccordionContent className="space-y-2 pb-3">
                    <Input
                      value={o.question}
                      onChange={(e) =>
                        setObjections((p) =>
                          p.map((x) =>
                            x.id === o.id ? { ...x, question: e.target.value } : x,
                          ),
                        )
                      }
                      placeholder='Ex.: "Está caro"'
                      maxLength={300}
                    />
                    <Textarea
                      value={o.answer_html.replace(/<[^>]+>/g, '')}
                      onChange={(e) =>
                        setObjections((p) =>
                          p.map((x) =>
                            x.id === o.id
                              ? { ...x, answer_html: e.target.value }
                              : x,
                          ),
                        )
                      }
                      placeholder="Resposta sugerida..."
                      rows={4}
                      maxLength={2000}
                    />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </TabsContent>
        </Tabs>

        <Separator />
        <SheetFooter className="p-3 shrink-0 flex-row justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>Salvar roteiro</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
